<template>
  <div class="lg:w-full chart-container"
    :style="{'min-height': minHeigth}"
    >
    <div class="chart-a11y-wrapper" :class="{'chart-focused': focusedBarIndex >= 0}">
      <canvas :id="id" aria-hidden="true"></canvas>
    </div>
    <div v-if="charData && charData.length"
      class="sr-only"
      role="listbox"
      :aria-label="label"
      :aria-roledescription="$t('charts.aria_roledescription')"
      @keydown="handleKeydown"
    >
      <div
        v-for="(item, index) in charData"
        :key="index"
        :id="id + '-bar-' + index"
        role="option"
        :tabindex="getBarTabindex(index)"
        :aria-selected="focusedBarIndex === index"
        @focus="onOptionFocus(index)"
        @blur="onOptionBlur($event)"
      >{{ (index + 1) + ' de ' + charData.length + ': ' + item.label + ', ' + item.value }}</div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'

export default {
  name: 'Chart',
  props: {
    type: {
      type: String,
      default: 'bar'
    },
    id: {
      type: String,
      default: 'chart'
    },
    label: {
      type: String,
      default: 'chart'
    },
    charData: {
      type: [Array, Object]
    }
  },
  data () {
    return {
      ChartData: null,
      myChart: null,
      focusedBarIndex: -1,
      isMouseOverChart: false,
      suppressMouseTooltip: false,
      onDocumentKeydown: null,
      onCanvasMouseEnter: null,
      onCanvasMouseLeave: null,
    }
  },
  computed: {
    minHeigth () {
      let heigth = 480
      if (this.charData) {
        const actualHeightAux = (this.charData.length + 2) * 28
        if (actualHeightAux > heigth) {
          heigth = actualHeightAux
        }
      }
      return heigth + 'px'
    },
    darkMode: function () {
      return this.$store.getters['app/darkMode']
    },
    infoResumenTexto () {
      var msg = ''
      if (this.charData) {
        msg += 'Notificaciones por estado. '
        this.charData.forEach(x => {
          msg += '' + x.label + ': ' + x.value + '; '
        })
      }
      return msg
    },
    infoResumenTextoFormat () {
      var msg = ''
      if (this.charData) {
        msg += '<b>Notificaciones por estado</b><br/>'
        this.charData.forEach(x => {
          msg += '' + x.label + ': ' + x.value + '<br/>'
        })
      }
      return msg
    }
  },
  watch: {
    charData: 'dataChangeHandler',
    darkMode: 'changeDarkmode'
  },
  mounted () {
    // WCAG 2.1.2 / 2.1.1: permitir cerrar el tooltip mostrado por hover del
    // ratón pulsando ESC, replicando el comportamiento del foco de teclado.
    this.onDocumentKeydown = (event) => {
      if ((event.key === 'Escape' || event.key === 'Esc') && this.isMouseOverChart) {
        this.hideMouseTooltip()
      }
    }
    document.addEventListener('keydown', this.onDocumentKeydown)
  },
  beforeDestroy () {
    if (this.onDocumentKeydown) {
      document.removeEventListener('keydown', this.onDocumentKeydown)
    }
    const ctx = document.getElementById(this.id)
    if (ctx) {
      if (this.onCanvasMouseEnter) ctx.removeEventListener('mouseenter', this.onCanvasMouseEnter)
      if (this.onCanvasMouseLeave) ctx.removeEventListener('mouseleave', this.onCanvasMouseLeave)
    }
  },
  methods: {
    getColor (property) {
      return getComputedStyle(document.querySelector(':root')).getPropertyValue(property)
    },
    getBarTabindex (index) {
      if (this.focusedBarIndex >= 0) return index === this.focusedBarIndex ? 0 : -1
      return index === 0 ? 0 : -1
    },
    focusBarOption (index) {
      const el = document.getElementById(this.id + '-bar-' + index)
      // preventScroll evita el salto nativo del navegador (las opciones sr-only
      // están al principio del contenedor); el centrado lo hace scrollBarIntoView.
      if (el) el.focus({ preventScroll: true })
    },
    getScrollParent (node) {
      let el = node ? node.parentElement : null
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el)
        const overflowY = style.overflowY
        if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && el.scrollHeight > el.clientHeight) {
          return el
        }
        el = el.parentElement
      }
      return null
    },
    scrollBarIntoView (index) {
      // WCAG 2.1 AA (2.4.7 Focus Visible): al enfocar una barra por teclado la
      // centramos en pantalla para que sea visible aunque el gráfico sea largo.
      if (!this.myChart) return
      const meta = this.myChart.getDatasetMeta(0)
      const bar = meta && meta.data ? meta.data[index] : null
      if (!bar) return
      const point = typeof bar.getCenterPoint === 'function' ? bar.getCenterPoint() : (bar._model || bar._view)
      const canvas = this.myChart.canvas
      if (!point || !canvas) return
      const barViewportY = canvas.getBoundingClientRect().top + point.y
      const scroller = this.getScrollParent(this.$el)
      if (scroller) {
        const scrollerRect = scroller.getBoundingClientRect()
        scroller.scrollBy({ top: barViewportY - (scrollerRect.top + scroller.clientHeight / 2), behavior: 'smooth' })
      } else {
        window.scrollBy({ top: barViewportY - (window.innerHeight / 2), behavior: 'smooth' })
      }
    },
    onOptionFocus (index) {
      this.focusedBarIndex = index
      this.highlightBar(index)
      this.scrollBarIntoView(index)
    },
    onOptionBlur (event) {
      const related = event.relatedTarget
      if (related && related.id && related.id.startsWith(this.id + '-bar-')) {
        return
      }
      this.clearHighlight()
      this.focusedBarIndex = -1
    },
    handleKeydown (event) {
      if (!this.myChart || !this.charData || this.charData.length === 0) return
      const totalBars = this.charData.length
      let newIndex = this.focusedBarIndex
      switch (event.key) {
        case 'Escape':
        case 'Esc':
          if (this.focusedBarIndex >= 0) {
            event.preventDefault()
            event.stopPropagation()
            this.clearHighlight()
          }
          return
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault()
          newIndex = (this.focusedBarIndex + 1) % totalBars
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault()
          newIndex = (this.focusedBarIndex - 1 + totalBars) % totalBars
          break
        case 'Enter':
        case ' ':
          event.preventDefault()
          if (this.focusedBarIndex >= 0) {
            this.$emit('callback', this.charData[this.focusedBarIndex].label)
          }
          return
        case 'Home':
          event.preventDefault()
          newIndex = 0
          break
        case 'End':
          event.preventDefault()
          newIndex = totalBars - 1
          break
        default:
          return
      }
      this.focusBarOption(newIndex)
    },
    highlightBar (index) {
      if (!this.myChart) return
      const meta = this.myChart.getDatasetMeta(0)
      if (!meta || !meta.data) return
      // Highlight focused bar, dim others
      const dataset = this.myChart.data.datasets[0]
      const totalBars = this.charData.length
      const dimColor = this.darkMode ? this.getColor('--primary-DEFAULT') + '55' : this.getColor('--primary-300') + '55'
      const highlightColor = this.getColor('--primary-600')
      const colors = []
      const borderColors = []
      const borderWidths = []
      for (let i = 0; i < totalBars; i++) {
        if (i === index) {
          colors.push(highlightColor)
          borderColors.push(this.getColor('--primary-600'))
          borderWidths.push(4)
        } else {
          colors.push(dimColor)
          borderColors.push(this.getColor('--primary-600'))
          borderWidths.push(2)
        }
      }
      dataset.backgroundColor = colors
      dataset.borderColor = borderColors
      dataset.borderWidth = borderWidths
      // Update colors first (with animation disabled to avoid flicker)
      this.myChart.update(0)
      // Show tooltip AFTER update so it doesn't get reset
      const activeElement = meta.data[index]
      if (activeElement) {
        this.myChart.tooltip._active = [activeElement]
        this.myChart.tooltip.update(true)
        this.myChart.draw()
      }
    },
    clearHighlight () {
      if (!this.myChart) return
      const dataset = this.myChart.data.datasets[0]
      if (!dataset) return
      const baseColor = this.darkMode ? this.getColor('--primary-DEFAULT') : this.getColor('--primary-300')
      dataset.backgroundColor = baseColor
      dataset.borderColor = this.getColor('--primary-600')
      dataset.borderWidth = 2
      // Update colors first, then hide tooltip
      this.myChart.update(0)
      this.myChart.tooltip._active = []
      this.myChart.tooltip.update(true)
      this.myChart.draw()
    },
    hideMouseTooltip () {
      // Cierra el tooltip mostrado por hover y evita que reaparezca hasta que
      // el ratón salga y vuelva a entrar en el canvas (WCAG 2.1.2 sin trampas).
      if (!this.myChart) return
      this.suppressMouseTooltip = true
      this.myChart.tooltip._active = []
      this.myChart.tooltip.update(true)
      this.myChart.draw()
    },
    changeDarkmode: function () {
      Chart.defaults.global.defaultFontColor = this.darkMode ? this.getColor('--secondary-100') : this.getColor('--secondary-DEFAULT') 
      this.myChart.config.options.scales.xAxes.forEach(x => {
        x.gridLines.color = this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
      }) 
      this.myChart.config.options.scales.yAxes.forEach(x => {
        x.gridLines.color = this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
        x.gridLines.zeroLineColor = this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
      }) 
      this.myChart.config.data.datasets.forEach(x => {
        x.backgroundColor = this.darkMode ? this.getColor('--primary-DEFAULT') : this.getColor('--primary-300') 
      })
      this.myChart.config.options.legend.labels.fontColor = this.darkMode ? this.getColor('--secondary-100') : this.getColor('--secondary-DEFAULT') 
      this.updateFixChart()
    },
    updateFixChart () {
      this.myChart.update()
      // console.log(this.myChart)
      setTimeout(() => {
        this.myChart.update()
        this.$emit('chartUpdated', this.infoResumenTextoFormat)
      }, 100)
    },
    removeData () {
      this.myChart.data.labels = []
      // this.myChart.data.datasets.forEach(x => x.data=[])
      for (let index = 0; index < this.myChart.data.datasets.length; index++) {
        this.myChart.data.datasets[index].data = []
      }
      // this.myChart.data.labels.pop()
      // this.myChart.data.datasets.forEach(x => x.data.pop())
      this.updateFixChart()
    },
    addData (label, data) {
      this.myChart.data.labels.push(label)
      this.myChart.data.datasets.forEach(x => x.data.push(data))
      this.updateFixChart()
    },
    dataChangeHandler: function () {
      this.focusedBarIndex = -1
      if (this.myChart !== null && this.myChart !== undefined) {
        this.removeData()
        this.charData.forEach(element => {
          this.addData(element.label, element.value)
        })
      } else {
        // this.$emit('toogleLoadingTop', true)
        Chart.defaults.global.defaultFontColor = this.darkMode ? this.getColor('--secondary-100') : this.getColor('--secondary-DEFAULT') 
        const ctx = document.getElementById(this.id)
        const labels = []
        const values = []
        this.charData.forEach(element => {
          labels.push(element.label)
          values.push(element.value)
        })
        this.ChartData = null
        this.ChartData = {
          type: this.type, // 'horizontalBar',
          data: {
            labels: labels,
            datasets: [
              {
                label: this.label,
                data: values,
                backgroundColor: this.darkMode ? this.getColor('--primary-DEFAULT') : this.getColor('--primary-300'),
                borderColor: this.getColor('--primary-600'),
                borderWidth: 2,
                maxBarThickness: 40,
                fontSize: 40,
              },
              // TMP Stacked 
              // {
              //   label: this.label,
              //   data: values,
              //   backgroundColor: 'black',
              //   borderColor: this.getColor('--primary-600') ,
              //   borderWidth: 2,
              //   maxBarThickness: 40,
              //   fontSize: 40,
              // }
            ]
          },
          options: {
            legend: {
              display: true,
              onClick: null,
              labels: {
                fontColor: this.darkMode ? this.getColor('--secondary-100') : this.getColor('--secondary-DEFAULT'),
                fontSize: 14,
              }
            },
            maintainAspectRatio: false,
            scales: {
              xAxes: [{
                stacked: true,
                gridLines: {
                  color: this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300'),
                },
                ticks: {
                  precision: 0,
                  fontSize: 14,
                }
              }],
              yAxes: [{
                // TMP Stacked 
                // stacked: true,
                gridLines: {
                  color: this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300'),
                  zeroLineColor: this.darkMode ? this.getColor('--darkblue-400') : this.getColor('--primary-300') 
                },
                ticks: {
                  beginAtZero: true,
                  fontSize: 14,
                }
              }]
            },
            onHover: (event, chartElement) => {
              const target = event.native ? event.native.target : event.target
              target.style.cursor = chartElement[0] ? 'pointer' : 'default'
            },
            tooltips: {
              // Permite suprimir el tooltip de hover tras pulsar ESC (WCAG 2.1.2).
              filter: () => !this.suppressMouseTooltip
            }
          }
        }
        // eslint-disable-next-line no-new
        const myChart = new Chart(ctx, this.ChartData)
        this.myChart = myChart
        const self = this
        this.onCanvasMouseEnter = () => {
          self.isMouseOverChart = true
        }
        this.onCanvasMouseLeave = () => {
          self.isMouseOverChart = false
          // Al salir el ratón, restablecemos el tooltip para el siguiente hover.
          self.suppressMouseTooltip = false
        }
        ctx.addEventListener('mouseenter', this.onCanvasMouseEnter)
        ctx.addEventListener('mouseleave', this.onCanvasMouseLeave)
        ctx.onclick = function (evt) {
          const activePoint = myChart.getElementAtEvent(evt)[0]
          if (activePoint && activePoint._chart) {
            const data = activePoint._chart.data
            const datasetIndex = activePoint._datasetIndex
            const label = data.labels[activePoint._index]
            const value = data.datasets[datasetIndex].data[activePoint._index]
            console.log(label, value)
            self.$emit('callback', label)
          }
        }
        this.updateFixChart()
      }
    }
  }
}
</script>

<style scoped>

.chart-container {
  position: relative;
  height: 42vh;
}

.chart-a11y-wrapper {
  width: 100%;
  height: 100%;
}

.chart-a11y-wrapper.chart-focused {
  outline: 3px solid var(--primary-600);
  outline-offset: 2px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
